/**
 * Lightweight React i18n for OceanHome admin/customer/owner frontend.
 *
 * Deliberately dependency-free — no react-i18next install required.
 *
 *   import { I18nProvider, useT, useLang, LanguageSwitcher } from '@/i18n';
 *
 *   // In App.js (or index.js):
 *   <I18nProvider>
 *     <App />
 *   </I18nProvider>
 *
 *   // In any component:
 *   const t = useT();
 *   return <h1>{t('dashboard.welcome', { name: user.name })}</h1>;
 *
 *   // Force a specific language (e.g. for admin pages that stay English):
 *   <I18nScope lang="en"><AdminPages /></I18nScope>
 *
 * Language persistence: localStorage key 'oh-lang'. Defaults to browser language
 * if supported, else 'en'. Admin and customer-service users should be pinned to
 * English via I18nScope at the route level (see App.js route groups).
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

import en from './locales/en.json';
import es from './locales/es.json';
import fi from './locales/fi.json';
import sv from './locales/sv.json';
import de from './locales/de.json';

const BUNDLES = { en, es, fi, sv, de };
export const SUPPORTED = ['en', 'es', 'fi', 'sv', 'de'];
export const LANG_META = {
  en: { code: 'en', flag: '🇬🇧', name: 'English'  },
  es: { code: 'es', flag: '🇪🇸', name: 'Español'  },
  fi: { code: 'fi', flag: '🇫🇮', name: 'Suomi'    },
  sv: { code: 'sv', flag: '🇸🇪', name: 'Svenska'  },
  de: { code: 'de', flag: '🇩🇪', name: 'Deutsch'  },
};

const STORAGE_KEY = 'oh-lang';

// ── Default-language detection ─────────────────────────────────────────────
function detectDefaultLang() {
  try {
    const stored = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(STORAGE_KEY)
      : null;
    if (stored && SUPPORTED.includes(stored)) return stored;
    if (typeof navigator !== 'undefined' && navigator.language) {
      const short = navigator.language.slice(0, 2).toLowerCase();
      if (SUPPORTED.includes(short)) return short;
    }
  } catch (_) { /* ignore */ }
  return 'en';
}

// ── Key resolver ───────────────────────────────────────────────────────────
// Dotted-path lookup with English fallback and {placeholder} interpolation.
function resolve(bundle, key) {
  if (!key) return '';
  const parts = key.split('.');
  let cur = bundle;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
    else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

function interpolate(str, vars) {
  if (!str || !vars) return str;
  return str.replace(/\{(\w+)\}/g, (_m, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

// Track already-warned keys so we don't spam the console.
const _warnedKeys = new Set();

function _humanise(key) {
  // Last segment of the dotted key, snake_case → Title Case.
  const last = key.split('.').pop() || key;
  return last.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function translate(lang, key, vars) {
  const bundle = BUNDLES[lang] || BUNDLES.en;
  let value = resolve(bundle, key);
  let usedFallback = false;
  if (value === undefined && lang !== 'en') {
    value = resolve(BUNDLES.en, key);
    usedFallback = value !== undefined;
  }
  if (value === undefined) {
    // Completely missing — warn in dev, humanise the last segment for users.
    if (process.env.NODE_ENV !== 'production' && !_warnedKeys.has(key)) {
      _warnedKeys.add(key);
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Missing key: "${key}" in "${lang}" and "en" fallback`);
    }
    return interpolate(_humanise(key), vars);
  }
  if (usedFallback && process.env.NODE_ENV !== 'production') {
    const warnKey = `${lang}:${key}`;
    if (!_warnedKeys.has(warnKey)) {
      _warnedKeys.add(warnKey);
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Missing key "${key}" in "${lang}" — using English fallback`);
    }
  }
  return interpolate(value, vars);
}

// ── Context ────────────────────────────────────────────────────────────────
const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (key, vars) => translate('en', key, vars),
});

export function I18nProvider({ children, initialLang }) {
  const [lang, setLangState] = useState(() => initialLang || detectDefaultLang());

  const setLang = useCallback((code) => {
    if (!SUPPORTED.includes(code)) return;
    setLangState(code);
    try { window.localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
    if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', code);
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t: (key, vars) => translate(lang, key, vars),
  }), [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Force a fixed language for a subtree (e.g. admin routes stay English even
// when the user's preferred lang is something else).
export function I18nScope({ lang, children }) {
  const parent = useContext(I18nContext);
  const value = useMemo(() => ({
    lang,
    setLang: parent.setLang,
    t: (key, vars) => translate(lang, key, vars),
  }), [lang, parent.setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}

export function useLang() {
  const ctx = useContext(I18nContext);
  return [ctx.lang, ctx.setLang];
}

// ── Language switcher component (drop-in) ──────────────────────────────────
export function LanguageSwitcher({ className = '' }) {
  const [lang, setLang] = useLang();
  return (
    <select
      value={lang}
      onChange={(e) => setLang(e.target.value)}
      className={className}
      aria-label="Language"
    >
      {SUPPORTED.map((code) => (
        <option key={code} value={code}>
          {LANG_META[code].flag} {LANG_META[code].name}
        </option>
      ))}
    </select>
  );
}
